import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatMoney } from "@bookboost/shared/format";

// Reusable deal-search combobox + a dialog wrapper for the linking flows.
// Searches the admin deals endpoint (closed-won deals only — the ones that
// matter for commission) and hands the caller the full deal row on select.

export interface DealPickerDeal {
  id: string;
  hubspot_deal_id: string;
  deal_name: string | null;
  company_name: string | null;
  amount: number | null;
  currency: string | null;
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function DealPicker({
  onSelect,
  placeholder = "Search deals by name / company / HubSpot id…",
  autoFocus = false,
}: {
  onSelect: (deal: DealPickerDeal) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 200);
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const term = debounced.trim();

  const { data, isFetching } = useQuery({
    queryKey: ["admin", "deals", "picker", term],
    queryFn: () =>
      api.get<{ deals: DealPickerDeal[] }>("/api/admin/deals", {
        searchParams: { q: term, pageSize: 10 },
      }),
    enabled: term.length > 0,
    staleTime: 30_000,
  });

  const deals = term.length > 0 ? data?.deals ?? [] : [];

  // Reset highlight when the result set changes.
  useEffect(() => setSelected(0), [term]);

  function pick(deal: DealPickerDeal) {
    setOpen(false);
    onSelect(deal);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setSelected((i) => Math.min(i + 1, Math.max(0, deals.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const d = deals[selected];
      if (open && d) pick(d);
    } else if (e.key === "Escape") {
      if (open) {
        // Swallow the first Escape to close the dropdown; a second one
        // propagates (e.g. closes an enclosing <dialog>).
        e.preventDefault();
        setOpen(false);
      }
    }
  }

  const showList = open && term.length > 0;

  return (
    <div className="dealpicker">
      <input
        ref={inputRef}
        className="confirm-input"
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && deals[selected] ? `${listId}-opt-${selected}` : undefined}
        aria-autocomplete="list"
        aria-label="Search deals"
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKey}
      />
      {showList && (
        <ul className="dealpicker-list" id={listId} role="listbox" aria-label="Matching deals">
          {deals.length === 0 ? (
            <li className="dealpicker-empty muted">
              {isFetching ? "Searching…" : "No closed-won deals match."}
            </li>
          ) : (
            deals.map((d, idx) => (
              <li
                key={d.id}
                id={`${listId}-opt-${idx}`}
                className={`dealpicker-item${idx === selected ? " selected" : ""}`}
                role="option"
                aria-selected={idx === selected}
                onMouseEnter={() => setSelected(idx)}
                // mousedown, not click, so the input doesn't blur first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(d);
                }}
              >
                <span className="dealpicker-name">{d.deal_name ?? "(unnamed deal)"}</span>
                <span className="dealpicker-sub muted">
                  {d.company_name ?? "—"}
                  {d.amount != null && d.currency ? ` · ${formatMoney(d.amount, d.currency)}` : ""}
                </span>
                <code className="dealpicker-id">{d.hubspot_deal_id}</code>
              </li>
            ))
          )}
        </ul>
      )}
      <style>{dealPickerStyles}</style>
    </div>
  );
}

// Modal wrapper used by the linking flows on /admin/invoices and
// /admin/customers. Selecting a deal resolves immediately; a manual-ID
// escape hatch covers deals that haven't synced into the DB yet.
export function DealPickerDialog({
  title,
  prompt,
  actionLabel = "Link",
  onPick,
  onClose,
}: {
  title: string;
  prompt: string;
  actionLabel?: string;
  onPick: (hubspotDealId: string, deal?: DealPickerDeal) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [manual, setManual] = useState(false);
  const [manualId, setManualId] = useState("");
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    previouslyFocused.current = document.activeElement;
    d.showModal();
    const onCancelEvt = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    d.addEventListener("cancel", onCancelEvt);
    return () => {
      d.removeEventListener("cancel", onCancelEvt);
      if (d.open) d.close();
      const prev = previouslyFocused.current;
      if (prev instanceof HTMLElement) {
        try {
          prev.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [onClose]);

  const canSubmitManual = manualId.trim().length > 0;

  return (
    <dialog ref={ref} className="confirm-dialog dealpicker-dialog" aria-labelledby="dealpicker-title">
      <div className="confirm-body">
        <h2 className="confirm-title" id="dealpicker-title">{title}</h2>
        <p className="confirm-prompt">{prompt}</p>
        {manual ? (
          <label className="confirm-input-label">
            <span className="confirm-input-text">HubSpot deal id</span>
            <input
              className="confirm-input"
              type="text"
              autoFocus
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmitManual) {
                  e.preventDefault();
                  onPick(manualId.trim());
                }
              }}
            />
          </label>
        ) : (
          <div style={{ width: "100%", marginBottom: 18 }}>
            <DealPicker autoFocus onSelect={(d) => onPick(d.hubspot_deal_id, d)} />
          </div>
        )}
        <div className="confirm-actions" style={{ justifyContent: "space-between" }}>
          <button
            type="button"
            className="dealpicker-manual-toggle"
            onClick={() => setManual((m) => !m)}
          >
            {manual ? "← Back to search" : "Enter ID manually"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="confirm-cancel" onClick={onClose}>
              Cancel
            </button>
            {manual && (
              <button
                type="button"
                className="primary confirm-ok"
                disabled={!canSubmitManual}
                onClick={() => onPick(manualId.trim())}
              >
                {actionLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
}

const dealPickerStyles = `
  .dealpicker { position: relative; width: 100%; }
  .dealpicker-list {
    position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 30;
    margin: 0; padding: 4px; list-style: none;
    background: var(--surface);
    border: 1px solid var(--border); border-radius: 10px;
    box-shadow: 0 12px 32px -8px rgba(16, 24, 40, 0.24);
    max-height: 280px; overflow-y: auto;
  }
  .dealpicker-empty { padding: 10px 12px; font-size: 12.5px; }
  .dealpicker-item {
    display: flex; flex-direction: column; gap: 1px;
    padding: 7px 10px; border-radius: 7px; cursor: pointer;
  }
  .dealpicker-item.selected { background: color-mix(in srgb, var(--text-strong) 6%, transparent); }
  .dealpicker-name { font-size: 13px; font-weight: 600; color: var(--text-strong); }
  .dealpicker-sub { font-size: 12px; }
  .dealpicker-id { font-size: 10.5px; }
  .dealpicker-dialog { max-width: 520px; }
  .dealpicker-manual-toggle {
    background: transparent; border: none; box-shadow: none; cursor: pointer;
    color: var(--muted); font-size: 12.5px; padding: 6px 4px; font-weight: 500;
  }
  .dealpicker-manual-toggle:hover { color: var(--text-strong); }
`;
